import axios from 'axios';

import * as constants from './constants';
import { addMessage } from '../../state/actions/messagesActions';

import { SurveyPayload, ErrorResponse } from '../../types/components/user-survey/Helpers';
import { AnswerConfig } from '../../types/components/user-survey/shared/Answer';
import store from '../../state/store';
type Store = typeof store;
type Dispatch = Store['dispatch'];

interface SurveyAnswersMap {
  [constants.ROLE_QUESTION_STEP]?: AnswerConfig[];
  [constants.EXPERIENCE_QUESTION_STEP]?: AnswerConfig[];
  [constants.HOW_INQUIRY_STEP]?: AnswerConfig[];
  [constants.SOFTWARE_QUESTION_STEP]?: AnswerConfig[];
}

// Step Navigation functions
export const getNextStep = (currentStep: number): number => {
  switch (currentStep) {
    case constants.INTRO:
      return constants.ROLE_QUESTION_STEP;
    case constants.ROLE_QUESTION_STEP:
      return constants.SOFTWARE_QUESTION_STEP;
    case constants.SOFTWARE_QUESTION_STEP:
      return constants.EXPERIENCE_QUESTION_STEP;
    case constants.EXPERIENCE_QUESTION_STEP:
      return constants.HOW_INQUIRY_STEP;
    default:
      return currentStep;
  }
};

export const getPreviousStep = (currentStep: number): number => {
  if (currentStep === constants.FIRST_STEP) return currentStep;

  switch (currentStep) {
    case constants.INTRO:
      return constants.FIRST_STEP;
    case constants.ROLE_QUESTION_STEP:
      return constants.FIRST_STEP;
    case constants.SOFTWARE_QUESTION_STEP:
      return constants.ROLE_QUESTION_STEP;
    case constants.EXPERIENCE_QUESTION_STEP:
      return constants.SOFTWARE_QUESTION_STEP;
    case constants.HOW_INQUIRY_STEP:
      return constants.EXPERIENCE_QUESTION_STEP;
    default:
      return currentStep;
  }
};

// Question prompt lookup
export const getQuestionPrompt = (stepIndex: number): string => {
  switch (stepIndex) {
    case constants.ROLE_QUESTION_STEP:
      return constants.ROLE_QUESTION_PROMPT;
    case constants.SOFTWARE_QUESTION_STEP:
      return constants.SOFTWARE_QUESTION_PROMPT;
    case constants.EXPERIENCE_QUESTION_STEP:
      return constants.EXPERIENCE_QUESTION_PROMPT;
    case constants.HOW_INQUIRY_STEP:
      return constants.HOW_INQUIRY_PROMPT;
    default:
      return '';
  }
};

// API handling
export const formatSurveyPayload = (answers: SurveyAnswersMap): SurveyPayload => {
  return {
    data: {
      type: 'market',
      role_in_vfx: answers[constants.ROLE_QUESTION_STEP][0]?.answerText || '',
      used_software:
        answers[constants.SOFTWARE_QUESTION_STEP]?.map((item: AnswerConfig) => item.answerText) ||
        [],
      experience_level: answers[constants.EXPERIENCE_QUESTION_STEP][0]?.answerText || '',
      how_discovered: answers[constants.HOW_INQUIRY_STEP][0]?.answerText || ''
    }
  };
};

export const submitSurveyAnswers = async (
  answers: SurveyAnswersMap,
  apiUrl: string,
  userToken: string,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void,
  dispatch: Dispatch
): Promise<boolean> => {
  setLoading(true);
  setError(null);

  try {
    const payload = formatSurveyPayload(answers);

    await axios.post(`${apiUrl}/users/surveys`, payload, {
      headers: { authorization: userToken }
    });

    setLoading(false);
    dispatch(addMessage({ body: 'Survey submitted successfully', type: 'success' }));
    return true;
  } catch (error: unknown) {
    const err = error as ErrorResponse;
    const errorMessage =
      err?.response?.data?.message ||
      err?.message ||
      'An error occurred while submitting the survey';
    dispatch(addMessage({ body: errorMessage, type: 'error' }));
    setError(errorMessage);
    setLoading(false);
    return false;
  }
};

// Multiple Choice Question Helpers
export const getAnswersForMultiChoiceStep = (stepIndex: number): AnswerConfig[] => {
  switch (stepIndex) {
    case constants.ROLE_QUESTION_STEP:
      return [
        { answerId: 1, answerText: constants.ROLE_QUESTION_ANSWER_1 },
        { answerId: 2, answerText: constants.ROLE_QUESTION_ANSWER_2 },
        { answerId: 3, answerText: constants.ROLE_QUESTION_ANSWER_3 },
        { answerId: 4, answerText: constants.ROLE_QUESTION_ANSWER_4 },
        { answerId: 5, answerText: constants.ROLE_QUESTION_ANSWER_5 },
        { answerId: 6, answerText: constants.ROLE_QUESTION_ANSWER_6 },
        { answerId: 7, answerText: constants.ROLE_QUESTION_ANSWER_7 },
        { answerId: 8, answerText: constants.ROLE_QUESTION_ANSWER_8 },
        { answerId: 9, answerText: constants.ROLE_QUESTION_ANSWER_9 },
        { answerId: 10, answerText: constants.ROLE_QUESTION_ANSWER_10 },
        { answerId: 11, answerText: constants.ROLE_QUESTION_ANSWER_11 },
        { answerId: 12, answerText: constants.ROLE_QUESTION_ANSWER_12 },
        { answerId: 13, answerText: constants.ROLE_QUESTION_ANSWER_13 }
      ];
    case constants.SOFTWARE_QUESTION_STEP:
      return [
        { answerId: 1, answerText: constants.SOFTWARE_QUESTION_ANSWER_1 },
        { answerId: 2, answerText: constants.SOFTWARE_QUESTION_ANSWER_2 },
        { answerId: 3, answerText: constants.SOFTWARE_QUESTION_ANSWER_3 },
        { answerId: 4, answerText: constants.SOFTWARE_QUESTION_ANSWER_4 },
        { answerId: 5, answerText: constants.SOFTWARE_QUESTION_ANSWER_5 },
        { answerId: 6, answerText: constants.SOFTWARE_QUESTION_ANSWER_6 },
        { answerId: 7, answerText: constants.SOFTWARE_QUESTION_ANSWER_7 },
        { answerId: 8, answerText: constants.SOFTWARE_QUESTION_ANSWER_8 },
        { answerId: 9, answerText: constants.SOFTWARE_QUESTION_ANSWER_9 },
        { answerId: 10, answerText: constants.SOFTWARE_QUESTION_ANSWER_10 },
        { answerId: 11, answerText: constants.SOFTWARE_QUESTION_ANSWER_11 },
        { answerId: 12, answerText: constants.SOFTWARE_QUESTION_ANSWER_12 },
        { answerId: 13, answerText: constants.SOFTWARE_QUESTION_ANSWER_13 },
        { answerId: 14, answerText: constants.SOFTWARE_QUESTION_ANSWER_14 }
      ];
    case constants.EXPERIENCE_QUESTION_STEP:
      return [
        { answerId: 1, answerText: constants.EXPERIENCE_QUESTION_ANSWER_1 },
        { answerId: 2, answerText: constants.EXPERIENCE_QUESTION_ANSWER_2 },
        { answerId: 3, answerText: constants.EXPERIENCE_QUESTION_ANSWER_3 },
        { answerId: 4, answerText: constants.EXPERIENCE_QUESTION_ANSWER_4 }
      ];
    case constants.HOW_INQUIRY_STEP:
      return [
        { answerId: 1, answerText: constants.HOW_INQUIRY_ANSWER_1 },
        { answerId: 2, answerText: constants.HOW_INQUIRY_ANSWER_2 },
        { answerId: 3, answerText: constants.HOW_INQUIRY_ANSWER_3 },
        { answerId: 4, answerText: constants.HOW_INQUIRY_ANSWER_4 },
        { answerId: 5, answerText: constants.HOW_INQUIRY_ANSWER_5 },
        { answerId: 6, answerText: constants.HOW_INQUIRY_ANSWER_6 },
        { answerId: 7, answerText: constants.HOW_INQUIRY_ANSWER_7 },
        { answerId: 8, answerText: constants.HOW_INQUIRY_ANSWER_8 },
        { answerId: 9, answerText: constants.HOW_INQUIRY_ANSWER_9 },
        { answerId: 10, answerText: constants.HOW_INQUIRY_ANSWER_10 }
      ];
    default:
      return [];
  }
};

export const splitMultiChoiceAnswers = (
  answers: AnswerConfig[]
): {
  leftColumn: AnswerConfig[];
  rightColumn: AnswerConfig[];
} => {
  if (answers.length <= 7) {
    return {
      leftColumn: answers,
      rightColumn: []
    };
  }

  if (answers.length % 2 === 0) {
    const midpoint = answers.length / 2;
    return {
      leftColumn: answers.slice(0, midpoint),
      rightColumn: answers.slice(midpoint)
    };
  } else {
    const leftCount = Math.min(7, Math.ceil(answers.length / 2));
    return {
      leftColumn: answers.slice(0, leftCount),
      rightColumn: answers.slice(leftCount)
    };
  }
};

// Multiple Select Question Helpers
export const getAnswersForMultiSelectStep = (): AnswerConfig[] => {
  return [
    { answerId: 1, answerText: constants.SOFTWARE_QUESTION_ANSWER_1 },
    { answerId: 2, answerText: constants.SOFTWARE_QUESTION_ANSWER_2 },
    { answerId: 3, answerText: constants.SOFTWARE_QUESTION_ANSWER_3 },
    { answerId: 4, answerText: constants.SOFTWARE_QUESTION_ANSWER_4 },
    { answerId: 5, answerText: constants.SOFTWARE_QUESTION_ANSWER_5 },
    { answerId: 6, answerText: constants.SOFTWARE_QUESTION_ANSWER_6 },
    { answerId: 7, answerText: constants.SOFTWARE_QUESTION_ANSWER_7 },
    { answerId: 8, answerText: constants.SOFTWARE_QUESTION_ANSWER_8 },
    { answerId: 9, answerText: constants.SOFTWARE_QUESTION_ANSWER_9 },
    { answerId: 10, answerText: constants.SOFTWARE_QUESTION_ANSWER_10 },
    { answerId: 11, answerText: constants.SOFTWARE_QUESTION_ANSWER_11 },
    { answerId: 12, answerText: constants.SOFTWARE_QUESTION_ANSWER_12 },
    { answerId: 13, answerText: constants.SOFTWARE_QUESTION_ANSWER_13 },
    { answerId: 14, answerText: constants.SOFTWARE_QUESTION_ANSWER_14 }
  ];
};

const helpers = {
  getNextStep,
  getPreviousStep,
  getQuestionPrompt,
  formatSurveyPayload,
  submitSurveyAnswers,
  getAnswersForMultiChoiceStep,
  splitMultiChoiceAnswers,
  getAnswersForMultiSelectStep
};

export default helpers;